'use client';

import { Box } from '@wishket/design-system';
import {
  ReactNode,
  forwardRef,
  CSSProperties,
  useState,
  useEffect,
  useRef,
} from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

import { PopoverArrow } from './BubblePopover.parts';

type Direction = 'up' | 'down' | 'left' | 'right';
type ArrowLeft = 'start' | '1/4' | '1/2' | '3/4' | 'end';
export interface BubblePopoverProps {
  variant?: Direction;
  content?: ReactNode;
  children?: ReactNode;
  className?: string;
  open?: boolean;
  style?: CSSProperties;
  isHighlight?: boolean;
}

interface ArrowLeftStyle {
  left: string;
  translate: string;
}
const DEFAULT_SCREEN_PADDING = 20;

const BubblePopover = forwardRef<HTMLDivElement, BubblePopoverProps>(
  (
    {
      variant = 'up',
      content,
      children,
      className,
      open = false,
      style,
      isHighlight = false,
    },
    ref
  ) => {
    const [isContentOpen, setIsContentOpen] = useState(false);
    const [arrowLeft, setArrowLeft] = useState<ArrowLeft | undefined>(
      undefined
    );
    const isAnimatable = !!arrowLeft;
    const contentRef = useRef<HTMLDivElement>(null);

    const initPopover = () => {
      setArrowLeft(undefined);
    };

    useEffect(() => {
      setIsContentOpen(open);
      if (!open) {
        initPopover();
      }
    }, [open]);

    useEffect(() => {
      if (isContentOpen && contentRef.current) {
        const { left, right, width } =
          contentRef.current.getBoundingClientRect();
        const screenWidth = window.innerWidth;
        const leftOverflowPercentage =
          (Math.max(0, -left + DEFAULT_SCREEN_PADDING) / width) * 100;
        const rightOverflowPercentage =
          (Math.max(0, right + DEFAULT_SCREEN_PADDING - screenWidth) / width) *
          100;

        if (leftOverflowPercentage > 25) {
          setArrowLeft('start');
        } else if (leftOverflowPercentage > 0) {
          setArrowLeft('1/4');
        } else if (rightOverflowPercentage > 25) {
          setArrowLeft('end');
        } else if (rightOverflowPercentage > 0) {
          setArrowLeft('3/4');
        } else {
          setArrowLeft('1/2');
        }
      }
    }, [isContentOpen]);

    const animateStyle =
      'absolute w-max transform select-none transition-popover duration-200 ease-in-out';
    const arrowLeftStyle: Record<ArrowLeft, ArrowLeftStyle> = {
      start: {
        left: 'left-[10%]',
        translate: '-translate-x-[10%]',
      },
      '1/4': {
        left: 'left-1/4',
        translate: '-translate-x-1/4',
      },
      '1/2': {
        left: 'left-1/2',
        translate: '-translate-x-1/2',
      },
      '3/4': {
        left: 'left-3/4',
        translate: '-translate-x-3/4',
      },
      end: {
        left: 'left-[90%]',
        translate: '-translate-x-[90%]',
      },
    };
    const boxStyle: Record<Direction, string> = {
      up: twJoin(
        'bottom-full left-1/2 mb-2 translate-y-4',
        isAnimatable ? arrowLeftStyle[arrowLeft].translate : '-translate-x-1/2'
      ),
      down: twJoin(
        'top-full left-1/2 mt-2 -translate-y-4',
        isAnimatable ? arrowLeftStyle[arrowLeft].translate : '-translate-x-1/2'
      ),
      left: 'right-full top-1/2 mr-2 -translate-x-0 -translate-y-1/2',
      right: 'left-full top-1/2 ml-2 -translate-y-1/2 translate-x-0',
    };
    const boxPositionAnimateStyleClass: Record<Direction, string> = {
      up: 'bottom-[calc(100%_+_16px)]',
      down: 'top-[calc(100%_+_16px)]',
      left: '-translate-x-4',
      right: 'translate-x-4',
    };
    const boxArrowStyle: Record<Direction, string> = {
      up: twJoin(
        '-bottom-[6px] -translate-x-1/2',
        isAnimatable ? arrowLeftStyle[arrowLeft].left : 'left-1/2'
      ),
      down: twJoin(
        '-top-[6px] -translate-x-1/2 rotate-180',
        isAnimatable ? arrowLeftStyle[arrowLeft].left : 'left-1/2'
      ),
      left: '-right-[9px] top-1/2 -translate-y-1/2 -rotate-90',
      right: '-left-[9px] top-1/2 -translate-y-1/2 rotate-90',
    };

    return (
      <>
        {children}
        {open && (
          <Box
            as={isHighlight ? 'span' : 'div'}
            className={twMerge('relative inline-block', className)}
            style={style}
          >
            <Box
              as={isHighlight ? 'span' : 'div'}
              className={twMerge(
                boxStyle[variant],
                animateStyle,
                isAnimatable && boxPositionAnimateStyleClass[variant],
                isAnimatable ? 'z-10 opacity-100' : 'z-0 opacity-0'
              )}
              ref={ref}
            >
              <PopoverArrow
                className={twMerge('absolute', boxArrowStyle[variant])}
              />
              <Box
                as={isHighlight ? 'span' : 'div'}
                className={twJoin(
                  'flex h-auto min-h-[28px] w-full rounded-xl bg-w-white shadow-graymedium',
                  isContentOpen ? 'pointer-events-auto' : 'pointer-events-none'
                )}
                ref={contentRef}
              >
                {content}
              </Box>
            </Box>
          </Box>
        )}
      </>
    );
  }
);

BubblePopover.displayName = 'BubblePopover';

export default BubblePopover;
